import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Camera,
  Database,
  Search,
  ShieldAlert,
  Tag,
  Network,
  Users,
  Award,
  Percent,
} from 'lucide-react';
import { useState } from 'react';

import type { AnalyticsSnapshot, FraudSignal, PricingSignal } from '@terra/api-client';
import {
  Badge,
  type BadgeTone,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  PageHeader,
  Spinner,
} from '@terra/ui';

import { api } from '../lib/api';

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const pct = (bps: number) => `${(bps / 100).toFixed(2)}%`;

function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: string[];
  active: string;
  onChange: (t: string) => void;
}) {
  return (
    <div className="border-border/60 bg-surface-subtle/30 mb-6 flex flex-wrap items-center gap-2 rounded-2xl border p-2 shadow-sm">
      {tabs.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => onChange(t)}
          className={`flex-1 rounded-xl px-4 py-2.5 text-[13px] font-bold transition-all sm:flex-none ${
            active === t
              ? 'bg-surface text-ink border-border/60 border shadow-sm'
              : 'text-ink-muted hover:bg-surface-subtle/50 hover:text-ink border border-transparent'
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: 'good' | 'bad' }) {
  return (
    <div className="border-border/60 bg-surface rounded-2xl border p-5 shadow-sm transition-all hover:shadow-md">
      <p className="text-ink-muted text-[13px] font-medium">{label}</p>
      <p
        className={`mt-2 text-2xl font-bold tracking-tight ${accent === 'good' ? 'text-emerald-600' : accent === 'bad' ? 'text-red-600' : 'text-ink'}`}
      >
        {value}
      </p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// ANALYTICS  (Phase 2.8 — dashboard, snapshots)
// ══════════════════════════════════════════════════════════════════

function DashboardTab() {
  const dash = useQuery({
    queryKey: ['analytics-dashboard'],
    queryFn: () => api.analytics.dashboard(),
  });

  if (dash.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if (!dash.data)
    return <EmptyState title="No data" description="Run a snapshot to populate analytics." />;

  const { platform, finance, ops } = dash.data;

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
          <Activity className="h-4 w-4" /> Platform
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="GMV" value={formatPaise(platform.gmvPaise)} />
          <Stat label="Orders" value={String(platform.orderCount)} />
          <Stat label="Unique Customers" value={String(platform.uniqueCustomerCount)} />
          <Stat label="SaaS Revenue" value={formatPaise(platform.saasRevenuePaise)} />
          <Stat label="Marketplace Sales" value={formatPaise(platform.marketplaceSalesPaise)} />
          <Stat label="POS Sales" value={formatPaise(platform.posSalesPaise)} />
          <Stat label="B2B Sales" value={formatPaise(platform.b2bSalesPaise)} />
        </div>
      </div>
      <div className="border-border/40 border-t pt-8">
        <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
          <Activity className="h-4 w-4" /> Finance
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Revenue" value={formatPaise(finance.revenuePaise)} />
          <Stat label="COGS" value={formatPaise(finance.cogsPaise)} />
          <Stat label="Opex" value={formatPaise(finance.operatingExpensePaise)} />
          <Stat
            label="Net"
            value={formatPaise(finance.netPaise)}
            accent={finance.netPaise >= 0 ? 'good' : 'bad'}
          />
          <Stat label="Gross Margin" value={pct(finance.grossMarginBps)} />
          <Stat label="Net Margin" value={pct(finance.netMarginBps)} />
          <Stat label="Burn Rate" value={formatPaise(finance.burnRatePaise)} />
        </div>
      </div>
      <div className="border-border/40 border-t pt-8">
        <h3 className="text-ink-muted mb-4 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
          <Activity className="h-4 w-4" /> Operations
        </h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Delivered" value={String(ops.deliveredCount)} />
          <Stat label="On-time" value={String(ops.onTimeCount)} />
          <Stat label="SLA" value={pct(ops.slaBps)} accent={ops.slaBps >= 9000 ? 'good' : 'bad'} />
          <Stat label="Settled GMV" value={formatPaise(ops.settledGmvPaise)} />
          <Stat label="Inventory Value" value={formatPaise(ops.inventoryValuePaise)} />
          <Stat label="Damage Write-off" value={formatPaise(ops.damagePaise)} accent="bad" />
        </div>
      </div>
      <div className="border-border/40 flex items-center justify-between border-t pt-4">
        <p className="text-ink-muted text-[12px] font-medium">
          Generated {new Date(dash.data.generatedAt).toLocaleString()}
        </p>
      </div>
    </div>
  );
}

function SnapshotsTab() {
  const qc = useQueryClient();
  const snapshots = useQuery({
    queryKey: ['analytics-snapshots'],
    queryFn: () => api.analytics.listSnapshots(),
  });
  const [period, setPeriod] = useState<'DAY' | 'WEEK' | 'MONTH' | 'YEAR'>('DAY');

  const run = useMutation({
    mutationFn: () => api.analytics.runSnapshot({ period }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['analytics-snapshots'] });
      void qc.invalidateQueries({ queryKey: ['analytics-dashboard'] });
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <div>
          <label className="text-ink mb-1 block text-sm font-medium">Period</label>
          <select
            className="border-border bg-surface rounded-md border px-3 py-2 text-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value as typeof period)}
          >
            {['DAY', 'WEEK', 'MONTH', 'YEAR'].map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <Button onClick={() => run.mutate()} loading={run.isPending}>
          Run snapshot
        </Button>
        {run.data && (
          <span className="text-sm text-emerald-600">
            {run.data.snapshotsWritten} snapshots written.
          </span>
        )}
      </div>

      {snapshots.isLoading ? (
        <div className="grid min-h-[300px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : (snapshots.data ?? []).length === 0 ? (
        <EmptyState title="No snapshots" description="Run a snapshot above." />
      ) : (
        <div className="border-border bg-surface overflow-x-auto rounded-lg border">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border bg-surface-subtle text-ink-muted border-b text-xs font-medium uppercase">
                <th className="px-4 py-3">Domain</th>
                <th className="px-4 py-3">Period</th>
                <th className="px-4 py-3">Window</th>
                <th className="px-4 py-3">Scope</th>
                <th className="px-4 py-3">Computed</th>
              </tr>
            </thead>
            <tbody>
              {(snapshots.data ?? []).map((s: AnalyticsSnapshot) => (
                <tr key={s.id} className="border-border border-b last:border-0">
                  <td className="px-4 py-3">
                    <Badge tone="neutral">{s.domain}</Badge>
                  </td>
                  <td className="px-4 py-3">{s.period}</td>
                  <td className="px-4 py-3 text-xs">
                    {new Date(s.periodStart).toLocaleDateString()} –{' '}
                    {new Date(s.periodEnd).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{s.scopeId ?? '—'}</td>
                  <td className="px-4 py-3 text-xs">{new Date(s.computedAt).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AnalyticsPage() {
  const [tab, setTab] = useState('Dashboard');
  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">Analytics</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Platform, finance and operations metrics with snapshot history.
          </p>
        </div>
      </div>
      <Tabs tabs={['Dashboard', 'Snapshots']} active={tab} onChange={setTab} />
      {tab === 'Dashboard' && <DashboardTab />}
      {tab === 'Snapshots' && <SnapshotsTab />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// INTELLIGENCE  (Phase 3.1 — 360 views, fraud, pricing, ETL)
// ══════════════════════════════════════════════════════════════════

const SEVERITY_TONE: Record<string, BadgeTone> = {
  LOW: 'neutral',
  MEDIUM: 'warning',
  HIGH: 'danger',
  CRITICAL: 'danger',
};
const FRAUD_STATUS_TONE: Record<string, BadgeTone> = {
  OPEN: 'warning',
  ACKNOWLEDGED: 'warning',
  ACTION_TAKEN: 'success',
  DISMISSED: 'neutral',
  FALSE_POSITIVE: 'neutral',
};
const PRICING_STATUS_TONE: Record<string, BadgeTone> = {
  PROPOSED: 'warning',
  APPLIED: 'success',
  REJECTED: 'danger',
  EXPIRED: 'neutral',
};
const HEALTH_TONE: Record<string, BadgeTone> = {
  A: 'success',
  B: 'success',
  C: 'warning',
  D: 'danger',
  F: 'danger',
};

function EtlTab() {
  const qc = useQueryClient();
  const run = useMutation({
    mutationFn: () => api.intelligence.runEtl({ limit: 1000 }),
    onSuccess: () => qc.invalidateQueries(),
  });
  return (
    <div className="border-border/60 bg-surface rounded-2xl border p-6 shadow-sm">
      <h3 className="text-ink-muted mb-3 flex items-center gap-2 text-[12px] font-bold uppercase tracking-wider">
        <Database className="h-4 w-4" /> Refresh 360 Datamarts
      </h3>
      <p className="text-ink-subtle mb-5 text-[13px] font-medium">
        Rebuilds Customer, Vendor, and Product 360 aggregates from source tables.
      </p>
      <Button onClick={() => run.mutate()} loading={run.isPending} className="px-6">
        Run ETL
      </Button>
      {run.data && (
        <div className="border-border/40 mt-6 grid grid-cols-2 gap-4 border-t pt-5 md:grid-cols-4">
          <div>
            <p className="text-ink-muted text-[12px] font-medium">Customers</p>
            <p className="text-ink text-lg font-bold">{run.data.customersRefreshed}</p>
          </div>
          <div>
            <p className="text-ink-muted text-[12px] font-medium">Vendors</p>
            <p className="text-ink text-lg font-bold">{run.data.vendorsRefreshed}</p>
          </div>
          <div>
            <p className="text-ink-muted text-[12px] font-medium">Products</p>
            <p className="text-ink text-lg font-bold">{run.data.productsRefreshed}</p>
          </div>
          <div>
            <p className="text-ink-muted text-[12px] font-medium">Duration</p>
            <p className="text-ink text-lg font-bold">{run.data.durationMs} ms</p>
          </div>
        </div>
      )}
    </div>
  );
}

function Lookup360Tab() {
  const [kind, setKind] = useState<'customer' | 'vendor' | 'product'>('customer');
  const [id, setId] = useState('');
  const [activeId, setActiveId] = useState('');

  const customer = useQuery({
    queryKey: ['intel-customer', activeId],
    queryFn: () => api.intelligence.getCustomer(activeId),
    enabled: kind === 'customer' && !!activeId,
  });
  const vendor = useQuery({
    queryKey: ['intel-vendor', activeId],
    queryFn: () => api.intelligence.getVendor(activeId),
    enabled: kind === 'vendor' && !!activeId,
  });
  const product = useQuery({
    queryKey: ['intel-product', activeId],
    queryFn: () => api.intelligence.getProduct(activeId),
    enabled: kind === 'product' && !!activeId,
  });

  const loading = customer.isLoading || vendor.isLoading || product.isLoading;

  return (
    <div className="space-y-4">
      <form
        className="flex items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setActiveId(id.trim());
        }}
      >
        <div>
          <label className="text-ink mb-1 block text-sm font-medium">Entity</label>
          <select
            className="border-border bg-surface rounded-md border px-3 py-2 text-sm"
            value={kind}
            onChange={(e) => {
              setKind(e.target.value as typeof kind);
              setActiveId('');
            }}
          >
            <option value="customer">Customer</option>
            <option value="vendor">Vendor</option>
            <option value="product">Product</option>
          </select>
        </div>
        <Input
          placeholder={`${kind} ID`}
          value={id}
          onChange={(e) => setId(e.target.value)}
          className="w-80 font-mono"
        />
        <Button type="submit">Load</Button>
      </form>

      {!activeId ? (
        <p className="text-ink-muted text-sm">Enter an ID to load its 360 view.</p>
      ) : loading ? (
        <div className="grid min-h-[300px] place-items-center">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          {kind === 'customer' && customer.data && (
            <Card>
              <CardBody>
                <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                  <div>
                    <p className="text-ink-muted text-xs">Lifecycle</p>
                    <Badge tone="neutral">{customer.data.lifecycleStage}</Badge>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Total orders</p>
                    <p className="font-semibold">{customer.data.totalOrders}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Total spend</p>
                    <p className="font-semibold">{formatPaise(customer.data.totalSpendPaise)}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">AOV</p>
                    <p className="font-semibold">{formatPaise(customer.data.avgOrderValuePaise)}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Repeat rate</p>
                    <p className="font-semibold">{pct(customer.data.repeatRateBps)}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Churn risk</p>
                    <p className="font-semibold">{pct(customer.data.churnRiskBps)}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Segments</p>
                    <p className="text-xs">{customer.data.segments.join(', ') || '—'}</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
          {kind === 'vendor' && vendor.data && (
            <Card>
              <CardBody>
                <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                  <div>
                    <p className="text-ink-muted text-xs">Health</p>
                    <Badge tone={HEALTH_TONE[vendor.data.healthGrade] ?? 'neutral'}>
                      {vendor.data.healthGrade}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">GMV lifetime</p>
                    <p className="font-semibold">{formatPaise(vendor.data.gmvLifetimePaise)}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">GMV 30d</p>
                    <p className="font-semibold">{formatPaise(vendor.data.gmv30dPaise)}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Orders</p>
                    <p className="font-semibold">{vendor.data.totalOrders}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Return rate</p>
                    <p className="font-semibold">{pct(vendor.data.returnRateBps)}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Fulfillment</p>
                    <p className="font-semibold">{pct(vendor.data.fulfillmentRateBps)}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">SLA</p>
                    <p className="font-semibold">{pct(vendor.data.slaBps)}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Avg fulfil days</p>
                    <p className="font-semibold">{vendor.data.avgFulfillmentDays}</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
          {kind === 'product' && product.data && (
            <Card>
              <CardBody>
                <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
                  <div>
                    <p className="text-ink-muted text-xs">Impressions</p>
                    <p className="font-semibold">{product.data.impressions}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Clicks</p>
                    <p className="font-semibold">{product.data.clicks}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">CTR</p>
                    <p className="font-semibold">{pct(product.data.ctrBps)}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Conversion</p>
                    <p className="font-semibold">{pct(product.data.conversionBps)}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Orders</p>
                    <p className="font-semibold">{product.data.orders}</p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Revenue</p>
                    <p className="font-semibold">
                      {formatPaise(product.data.revenueLifetimePaise)}
                    </p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Rating</p>
                    <p className="font-semibold">
                      {(product.data.avgRatingX100 / 100).toFixed(2)} ({product.data.ratingCount})
                    </p>
                  </div>
                  <div>
                    <p className="text-ink-muted text-xs">Stock</p>
                    <p className="font-semibold">{product.data.stockOnHand}</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function FraudTab() {
  const qc = useQueryClient();
  const signals = useQuery({
    queryKey: ['intel-fraud'],
    queryFn: () => api.intelligence.listFraud(),
  });

  const resolve = useMutation({
    mutationFn: ({
      id,
      status,
    }: {
      id: string;
      status: 'ACTION_TAKEN' | 'DISMISSED' | 'FALSE_POSITIVE';
    }) => api.intelligence.resolveFraud(id, { status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['intel-fraud'] }),
  });

  if (signals.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((signals.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<ShieldAlert className="text-ink-muted h-10 w-10" />}
          title="No fraud signals"
          description="Flagged signals will appear here."
        />
      </div>
    );

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="px-4 py-3 pl-5">Entity</th>
              <th className="px-4 py-3">Kind</th>
              <th className="px-4 py-3 text-center">Score</th>
              <th className="px-4 py-3 text-center">Severity</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 pr-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {(signals.data ?? []).map((s: FraudSignal) => (
              <tr key={s.id} className="hover:bg-surface-subtle/30 group transition-colors">
                <td className="px-4 py-3 pl-5">
                  <div className="flex items-center gap-2">
                    <Badge tone="neutral">{s.entityType}</Badge>
                    <span className="font-mono text-[12px] font-semibold">
                      {s.entityId.slice(0, 10)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-3 font-semibold">{s.kind}</td>
                <td className="text-ink px-4 py-3 text-center font-bold">{s.score}</td>
                <td className="px-4 py-3 text-center">
                  <Badge tone={SEVERITY_TONE[s.severity] ?? 'neutral'}>{s.severity}</Badge>
                </td>
                <td className="px-4 py-3 text-center">
                  <Badge tone={FRAUD_STATUS_TONE[s.status] ?? 'neutral'}>
                    {s.status.replace(/_/g, ' ')}
                  </Badge>
                </td>
                <td className="px-4 py-3 pr-5 text-right">
                  <div className="flex justify-end gap-2">
                    {(s.status === 'OPEN' || s.status === 'ACKNOWLEDGED') && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => resolve.mutate({ id: s.id, status: 'ACTION_TAKEN' })}
                        >
                          Action
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolve.mutate({ id: s.id, status: 'DISMISSED' })}
                        >
                          Dismiss
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => resolve.mutate({ id: s.id, status: 'FALSE_POSITIVE' })}
                        >
                          False+
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PricingTab() {
  const qc = useQueryClient();
  const signals = useQuery({
    queryKey: ['intel-pricing'],
    queryFn: () => api.intelligence.listPricing(),
  });

  const resolve = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: 'APPLY' | 'REJECT' }) =>
      api.intelligence.resolvePricing(id, decision),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['intel-pricing'] }),
  });

  if (signals.isLoading)
    return (
      <div className="grid min-h-[300px] place-items-center">
        <Spinner size="lg" />
      </div>
    );
  if ((signals.data ?? []).length === 0)
    return (
      <div className="border-border/60 bg-surface rounded-2xl border p-12 shadow-sm">
        <EmptyState
          icon={<Percent className="text-ink-muted h-10 w-10" />}
          title="No pricing signals"
          description="Proposed pricing signals will appear here."
        />
      </div>
    );

  return (
    <div className="border-border/60 bg-surface overflow-hidden rounded-2xl border shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-border/60 text-ink-muted border-b text-[10px] font-bold uppercase tracking-wider">
              <th className="px-4 py-3 pl-5">Variant</th>
              <th className="px-4 py-3">Kind</th>
              <th className="px-4 py-3 text-right">Discount</th>
              <th className="px-4 py-3 text-right">Confidence</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 pr-5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-border/40 divide-y text-[13px]">
            {(signals.data ?? []).map((s: PricingSignal) => (
              <tr key={s.id} className="hover:bg-surface-subtle/30 group transition-colors">
                <td className="px-4 py-3 pl-5 font-mono text-[12px] font-semibold">
                  {s.variantId.slice(0, 10)}…
                </td>
                <td className="px-4 py-3 font-semibold">{s.kind.replace(/_/g, ' ')}</td>
                <td className="text-ink px-4 py-3 text-right font-bold">
                  {pct(s.suggestedDiscountBps)}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-emerald-600">
                  {pct(s.confidenceBps)}
                </td>
                <td className="px-4 py-3 text-center">
                  <Badge tone={PRICING_STATUS_TONE[s.status] ?? 'neutral'}>{s.status}</Badge>
                </td>
                <td className="px-4 py-3 pr-5 text-right">
                  <div className="flex justify-end gap-2">
                    {s.status === 'PROPOSED' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => resolve.mutate({ id: s.id, decision: 'APPLY' })}
                        >
                          Apply
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => resolve.mutate({ id: s.id, decision: 'REJECT' })}
                        >
                          Reject
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function IntelligencePage() {
  const [tab, setTab] = useState('360 Views');
  return (
    <div className="space-y-6 pb-8">
      <div className="border-border/60 bg-surface flex flex-col justify-between gap-4 rounded-2xl border p-5 shadow-sm sm:flex-row sm:items-center">
        <div>
          <h3 className="text-ink text-[16px] font-bold tracking-tight">Intelligence</h3>
          <p className="text-ink-subtle mt-0.5 text-[13px] font-medium">
            Customer/Vendor/Product 360 views, fraud signals, dynamic pricing and ETL.
          </p>
        </div>
      </div>
      <Tabs tabs={['360 Views', 'Fraud', 'Pricing', 'ETL']} active={tab} onChange={setTab} />
      {tab === '360 Views' && <Lookup360Tab />}
      {tab === 'Fraud' && <FraudTab />}
      {tab === 'Pricing' && <PricingTab />}
      {tab === 'ETL' && <EtlTab />}
    </div>
  );
}
